import React, { useEffect, useRef, useState } from 'react';
import Chart from 'react-apexcharts';

const PieChart = ({ remainingBalance, originalBalance, uom, width, height }) => {
    const chartRef = useRef(null);
    const [chartKey, setChartKey] = useState(0);
    const [isVisible, setIsVisible] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isInViewport, setIsInViewport] = useState(false);

    const observerRef = useRef(null);
    const containerRef = useRef(null);

    // Detect infinite/unlimited balance
    const isInfiniteBalance =
        originalBalance >= Number.MAX_VALUE || remainingBalance >= Number.MAX_VALUE;

    // Normalization function (if needed later for KB, MB, GB, etc.)
    const normalizeBalance = (balance, unit) => {
        switch (unit) {
            case 'SECOND': return balance; // leave as is, but could convert to minutes/hours if needed
            case 'KB': return balance / 1024;
            case 'GB': return balance * 1024;
            default: return balance; // MB or already normalized
        }
    };

    let safeOriginal = normalizeBalance(originalBalance, uom);
    let safeRemaining = normalizeBalance(remainingBalance, uom);

    // Handle unlimited
    if (isInfiniteBalance) {
        safeOriginal = 100;
        safeRemaining = 100;
    }

    let remainingPercentage = safeOriginal > 0
        ? (safeRemaining / safeOriginal) * 100
        : 0;

    // Make sure very small values still visible
    if (remainingPercentage > 0 && remainingPercentage < 1) {
        remainingPercentage = 1;
    }

    const isBalanceConsumed = safeRemaining <= 0 || (safeOriginal > 0 && safeRemaining === 0);

    useEffect(() => {
        if (containerRef.current) {
            observerRef.current = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        setIsInViewport(entry.isIntersecting);
                    });
                },
                {
                    threshold: 0.1,
                    rootMargin: '50px'
                }
            );

            observerRef.current.observe(containerRef.current);
        }

        return () => {
            if (observerRef.current && containerRef.current) {
                observerRef.current.unobserve(containerRef.current);
            }
        };
    }, []);

    useEffect(() => {
        setIsLoading(true);
        setIsVisible(false);

        if (isBalanceConsumed) {
            setIsLoading(false);
            setIsVisible(true);
            return;
        }

        const timeout = setTimeout(() => {
            if (isInViewport) {
                setIsVisible(true);
                setIsLoading(false);
            }
        }, 300);

        return () => clearTimeout(timeout);
    }, [remainingBalance, originalBalance, isInViewport]);

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible' && isInViewport) {
                setTimeout(() => {
                    setChartKey(prev => prev + 1);
                    setIsVisible(false);
                    setIsLoading(true);
                    
                    setTimeout(() => {
                        setIsVisible(true);
                        setIsLoading(false);
                    }, 100);
                }, 200);
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, [isInViewport]);

    useEffect(() => {
        if (isInViewport && !isVisible && !isBalanceConsumed) {
            const timeout = setTimeout(() => {
                setIsVisible(true);
                setIsLoading(false);
            }, 150);
            return () => clearTimeout(timeout);
        }
    }, [isInViewport, isVisible, isBalanceConsumed]);

    let tooltipEl = document.getElementById("global-apex-tooltip");
    if (!tooltipEl) {
        tooltipEl = document.createElement("div");
        tooltipEl.id = "global-apex-tooltip";
        tooltipEl.style.position = "fixed"; // fixed ensures no layout shift
        tooltipEl.style.pointerEvents = "none";
        tooltipEl.style.zIndex = "9999";
        tooltipEl.style.display = "none"; // hidden by default
        tooltipEl.style.transition = "top 0.05s, left 0.05s";
        document.body.appendChild(tooltipEl);
    }

    const options = {
        chart: {
            type: 'donut',
            height: height || 160,
            offsetX: 30,
            parentHeightOffset: 0,
            animations: {
                enabled: true,
                easing: 'easeinout',
                speed: 800,
                animateGradually: { enabled: true, delay: 150 },
                dynamicAnimation: { enabled: true, speed: 350 }
            },
            events: {
                mounted: function() {
                    // Ensure loading state is cleared when chart mounts
                    setTimeout(() => setIsLoading(false), 100);
                },
                animationEnd: function () {
                    setTimeout(() => setIsLoading(false), 100);
                },
                dataPointMouseEnter: function (event, chartContext, config) {
                    const dataPointIndex = config.dataPointIndex; // Index of the data point (0 or 1)
                    const value = config.w.globals.series[dataPointIndex]; // Get the value directly
                    const label = dataPointIndex === 1 ? 'Remaining Balance' : 'Consumed Balance';
                    const backgroundColor = dataPointIndex === 0 ? '#FFFFFF' : '#FD7545';
                    const textColor = dataPointIndex === 0 ? '#000000' : '#FFFFFF';
                    tooltipEl.innerHTML = `
                    <div style="
                        background:${backgroundColor};
                        color:${textColor};
                        padding:6px 12px;
                        border-radius:6px;
                        white-space:nowrap;
                        font-weight:400;
                        font-size:12px;
                        box-shadow:0 2px 6px rgba(0,0,0,0.2);
                    ">
                        ${label}: <span style="font-weight:600;">${value}</span>
                    </div>
                    `;
                    tooltipEl.style.display = "block";
                    const chartEl = chartContext.el;
                    const moveHandler = (e) => {
                        tooltipEl.style.top = e.clientY + 15 + "px";
                        tooltipEl.style.left = e.clientX + 15 + "px";
                    };
                    chartEl.addEventListener("mousemove", moveHandler);
                    chartEl.addEventListener(
                        "mouseleave",
                        () => {
                            tooltipEl.style.display = "none";
                            chartEl.removeEventListener("mousemove", moveHandler);
                        },
                        { once: true }
                    );
                },
                dataPointMouseLeave: function () {
                    tooltipEl.style.display = "none";
                    tooltipEl.onmousemove = null; // stop following mouse
                }
            }
        },
        labels: ['Consumed Balance', 'Remaining Balance'],
        colors: ['#FFFFFF', '#FD7545'],
        dataLabels: { enabled: false },
        grid: { padding: { right: 50 } },
        legend: { show: false },
        plotOptions: {
            pie: { donut: { size: '65%', background: 'transparent' } }
        },
        tooltip:{
            enabled: false
        }
    };

    const series = [
        Math.max(0, 100 - remainingPercentage),
        Math.max(0, remainingPercentage)
    ];

    const Loader = () => (
        <div
            className="d-flex align-items-center justify-content-center position-absolute"
            style={{
                width:  200,
                height:  200,
                top: 0,
                left: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                zIndex: 10,
                borderRadius: '50%'
            }}
        >
            <div
                className="spinner-border text-primary"
                role="status"
                style={{ width: '2rem', height: '2rem', borderWidth: '3px' }}
            >
                <span className="visually-hidden">Loading...</span>
            </div>
        </div>
    );

    const BalanceConsumedMessage = () => (
        <div
            className="d-flex align-items-center justify-content-center"
            style={{
                width: 150,
                height: 150,
                backgroundColor: '#f8f9fa',
                borderRadius: '50%',
                border: '3px solid #dc3545',
                flexDirection: 'column',
                textAlign: 'center'
            }}
        >
            <div className='fs-1 fw-bold'>0%</div>
        </div>
    );

    if (isBalanceConsumed) {
        return (
            <div className="pie-chart position-relative" ref={containerRef}>
                <BalanceConsumedMessage />
            </div>
        );
    }

    return (
        <div className="pie-chart position-relative" ref={containerRef} style={{ position: 'relative' }}>
            {isLoading && <Loader />}
            <div
                ref={chartRef}
                style={{
                    opacity: isLoading ? 0.3 : 1,
                    transition: 'opacity 0.3s ease-in-out',
                    marginLeft: '-25px',
                }}
            >
                {isVisible && isInViewport && (
                    <Chart
                        ref={chartRef}
                        key={chartKey}
                        options={options}
                        series={series}
                        type="donut"
                        width={width}
                        height={height}
                    />
                )}

                {/* Center label */}
                <div
                    style={{
                        position: 'absolute',
                        left: '50%',
                        top: '50%',
                        transform: 'translate(-50%, -50%)',
                        fontSize: '20px',
                        fontWeight: 'bold',
                        color: '#000',
                        pointerEvents: 'none',
                        opacity: isLoading ? 0 : 1,
                        transition: 'opacity 0.3s ease-in-out'
                    }}
                >
                    {isInfiniteBalance ? '100%' : `${parseFloat(remainingPercentage.toFixed(2)).toString()}%`}
                </div>
            </div>
        </div>
    );
};

export default PieChart;
